import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import swal from "../../services/swalConfig";
import * as XLSX from "xlsx";
import {
  cleanNumericValue,
  formatDisplayNumber,
} from "../utilities/CleanNumeric";

import { tr } from "../../i18n";
export default function ModalAssignItemsPayroll({
  show,
  onClose,
  payrollData,
  assignedItems = [],
  onSubmit,
  onDeleteItem,
}) {
  const [rows, setRows] = useState([
    {
      id: 1,
      email: "",
      itemName: "",
      value: "",
      type: "income",
      operator: "add",
      isTaxable: "false",
      groupName: "",
      isExisting: false,
    },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load existing assigned items when modal opens
  useEffect(() => {
    if (show) {
      if (assignedItems.length > 0) {
        // Convert assigned items to rows format
        const existingRows = assignedItems.map((item) => ({
          id: item.id,
          email: item.email,
          itemName: item.itemName,
          value: item.value,
          type: item.type,
          operator: item.operator,
          isTaxable: item.isTaxable ? "true" : "false",
          groupName: item.groupName,
          isExisting: true,
        }));

        // Add one empty row at the end
        const newEmptyRow = {
          id: Date.now(),
          email: "",
          itemName: "",
          value: "",
          type: "income",
          operator: "add",
          isTaxable: "false",
          groupName: "",
          isExisting: false,
        };

        setRows([...existingRows, newEmptyRow]);
      } else {
        // No existing items, just show one empty row
        setRows([
          {
            id: Date.now(),
            email: "",
            itemName: "",
            value: "",
            type: "income",
            operator: "add",
            isTaxable: "false",
            groupName: "",
            isExisting: false,
          },
        ]);
      }
    }
  }, [show, assignedItems]);

  const addRow = () => {
    const newRow = {
      id: Date.now(),
      email: "",
      itemName: "",
      value: "",
      type: "income",
      operator: "add",
      isTaxable: "false",
      groupName: "",
      isExisting: false,
    };
    setRows([...rows, newRow]);
  };

  const deleteRow = (id) => {
    const rowToDelete = rows.find((row) => row.id === id);

    // If it's an existing item, show confirmation and mark for deletion
    if (rowToDelete && rowToDelete.isExisting) {
      if (
        window.confirm(
          tr("payroll:this_item_will_be_removed_when"),
        )
      ) {
        // Just remove from local rows, will be processed on submit
        setRows(rows.filter((row) => row.id !== id));
      }
    } else {
      // For new rows, just check if there's at least one row left
      const nonExistingRows = rows.filter((row) => !row.isExisting);
      if (
        nonExistingRows.length === 1 &&
        rows.filter((r) => r.email && r.itemName && r.value).length === 0
      ) {
        alert("You must have at least one empty row");
        return;
      }
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const updateRow = (id, field, value) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const handleSubmit = async () => {
    // Get all valid rows
    const allValidRows = rows.filter(
      (row) => row.email && row.itemName && row.value,
    );

    // Allow submit if there are changes (even if no valid rows - for deletion case)
    const hasExistingItems = assignedItems.length > 0;

    if (allValidRows.length === 0 && !hasExistingItems) {
      alert(
        tr("payroll:please_add_at_least_one_valid"),
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Transform items to items format
      const itemsByEmail = {};

      // Process ALL valid rows (both existing and new)
      allValidRows.forEach((row) => {
        if (!itemsByEmail[row.email]) {
          itemsByEmail[row.email] = [];
        }

        itemsByEmail[row.email].push({
          name: row.itemName,
          formula: row.value,
          type: row.type,
          operator: row.operator,
          value: cleanNumericValue(row.value),
          is_taxable: row.isTaxable === "true",
          group_name: row.groupName || "Additional Items",
        });
      });

      // Submit (even if empty object - to handle deletion case)
      await onSubmit(itemsByEmail);
    } catch (error) {
      console.error("Error submitting:", error);
      alert(tr("payroll:failed_to_submit_please_try_again"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!show) return null;

  const existingRowsCount = rows.filter((r) => r.isExisting).length;
  const validRowsCount = rows.filter(
    (r) => r.email && r.itemName && r.value,
  ).length;
  const newValidRowsCount = rows.filter(
    (r) => !r.isExisting && r.email && r.itemName && r.value,
  ).length;

  // Check if there are changes (deletions or new items)
  const hasChanges =
    existingRowsCount > 0 || newValidRowsCount > 0 || assignedItems.length > 0;

  const isValidEmail = (email, payrollData) => {
    return payrollData.some((p) => p.email === email);
  };

  // Cari email karyawan dari baris Excel, baik lewat kolom Email ATAU lewat
  // Internal ID karyawan (company_personnel.internal_id) — istilah generik yang
  // dipakai Sewoork lintas company, apa pun sebutan lokalnya di client (NIP, NIK,
  // employee code, badge number, dst). Kolom "Internal ID" jadi nama utama;
  // NIP/NIK/Employee ID tetap didukung sebagai alias untuk kompatibilitas.
  // Mengembalikan null kalau tidak ketemu di salah satu cara.
  const resolveEmployeeEmail = (row, payrollData) => {
    const email = (row.Email || "").toString().trim();
    if (email && isValidEmail(email, payrollData)) {
      return email;
    }

    const identifier = (
      row["Internal ID"] ||
      row.InternalID ||
      row.NIP ||
      row.NIK ||
      row["Employee ID"] ||
      row.EmployeeID ||
      ""
    )
      .toString()
      .trim();

    if (!identifier) {
      return null;
    }

    const match = payrollData.find(
      (p) =>
        p.internal_id != null &&
        p.internal_id.toString().trim() === identifier,
    );

    return match ? match.email : null;
  };

  // Fungsi Download Template
  const handleDownloadTemplate = () => {
    const template = [
      {
        Email: "example@company.com",
        "Internal ID": "",
        ItemName: "Bonus Project",
        Value: "500000",
        Type: "income",
        Operator: "add",
        IsTaxable: "false",
        GroupName: "Rewards",
      },
      {
        Email: "",
        "Internal ID": "1001",
        ItemName: "Tunjangan Jabatan",
        Value: "1000000",
        Type: "income",
        Operator: "add",
        IsTaxable: "true",
        GroupName: "Pendapatan Tetap",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Payroll_Assign_Template.xlsx");
  };

  // Fungsi Import Excel
  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        // defval: "" memastikan jika kolom kosong, dia tidak undefined
        const jsonData = XLSX.utils.sheet_to_json(ws, { defval: "" });

        const newRows = [];
        const errors = [];

        jsonData.forEach((row, index) => {
          const line = index + 2; // Baris di Excel untuk info error

          // Sesuaikan 'Value' dengan header di Excel Anda (misal 'Value/Formula' atau 'Value')
          const rawValue = row.Value || row["Value/Formula"] || "";
          const cleanedValue = cleanNumericValue(rawValue);

          // --- VALIDASI ---
          // Identifier karyawan bisa lewat Email ATAU NIP/NIK/Internal ID/Employee ID
          // (dicocokkan ke company_personnel.internal_id).
          const resolvedEmail = resolveEmployeeEmail(row, payrollData);
          if (!resolvedEmail) {
            const identifierShown =
              row.Email ||
              row["Internal ID"] ||
              row.InternalID ||
              row.NIP ||
              row.NIK ||
              row["Employee ID"] ||
              row.EmployeeID ||
              "(empty)";
            errors.push(
              `Row ${line}: Employee identifier '${identifierShown}' not found (checked Email and Internal ID)`,
            );
          }
          if (!row.ItemName && !row["Item Name"]) {
            errors.push(`Row ${line}: Item Name is required`);
          }
          if (cleanedValue === "") {
            errors.push(
              `Row ${line}: Value/Formula is required (Current: ${rawValue})`,
            );
          }

          const type = (row.Type || "").toLowerCase();
          if (!["income", "deduction"].includes(type)) {
            errors.push(`Row ${line}: Type must be income/deduction`);
          }

          const operator = (row.Operator || "").toLowerCase();
          if (!["add", "subtract"].includes(operator)) {
            errors.push(`Row ${line}: Operator must be add/subtract`);
          }

          // Jika sampai baris ini tidak ada error, masukkan ke array penampung
          if (errors.length === 0) {
            newRows.push({
              id: Date.now() + index,
              email: resolvedEmail,
              itemName: row.ItemName || row["Item Name"],
              value: cleanedValue, // Sudah bersih dari koma ribuan
              type: type,
              operator: operator,
              isTaxable:
                String(row.IsTaxable || row["Is Taxable"]).toLowerCase() ===
                "true"
                  ? "true"
                  : "false",
              groupName: row.GroupName || row["Group Name"] || "",
              isExisting: false,
            });
          }
        });

        if (errors.length > 0) {
          // Tampilkan maksimal 5 error agar tidak menutupi layar
          const showErrors = errors.slice(0, 5).join("<br/>");
          swal.fire(
            tr("payroll:import_error"),
            `${showErrors} ${errors.length > 5 ? "<br/>...and more" : ""}`,
            "error",
          );
        } else {
          // Filter baris yang benar-benar kosong saja sebelum digabung
          const filteredCurrentRows = rows.filter(
            (r) => r.email !== "" || r.itemName !== "",
          );
          setRows([...filteredCurrentRows, ...newRows]);

          swal.fire(
            tr("shared:success_2"),
            tr("payroll:count_items_imported_successfully", { count: newRows.length }),
            "success",
          );
        }
      } catch (err) {
        console.error(err);
        swal.fire(tr("shared:error"), tr("payroll:failed_to_read_excel_file"), "error");
      }

      e.target.value = null; // Reset input file
    };

    reader.readAsArrayBuffer(file); // Gunakan ArrayBuffer untuk stabilitas data
  };

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 1050,
      }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-xl"
        style={{ maxWidth: "95%", marginTop: "30px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-plus-circle me-2"></i>
              {tr("payroll:assign_items_to_employees")}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isSubmitting}
            ></button>
          </div>

          <div
            className="modal-body"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <div className="alert alert-info mb-3">
              <i className="fas fa-info-circle me-2"></i>
              <strong>{tr("payroll:instructions")}</strong> {" "}{tr("payroll:fill_in_the_form_below_to")}{" "} <strong>{tr("payroll:add_row_2")}</strong> {" "}{tr("payroll:button_to_add_more_rows")}
              <br />
              <i className="fas fa-file-excel me-2 mt-2"></i>
              <strong>{tr("payroll:excel_import")}</strong> {" "}{tr("payroll:identify_each_employee_by")}{" "}
              <strong>{tr("shared:email")}</strong> {" "}{tr("payroll:or")}{" "} <strong>{tr("shared:internal_id")}</strong> {" "}{tr("payroll:column_matched_against_each_employee_s")}
              {existingRowsCount > 0 && (
                <>
                  <br />
                  <i className="fas fa-edit me-2 mt-2"></i>
                  <strong>{tr("payroll:note")}</strong> {" "}{tr("payroll:rows_with")}{" "}
                  <span className="badge bg-secondary">{tr("payroll:existing")}</span> {" "}{tr("payroll:label_are_previously_assigned_items_you")}
                </>
              )}
            </div>

            <div className="mb-3 d-flex justify-content-between align-items-center">
              {/* Sisi Kiri: Add Row & Stats */}
              <div>
                <button
                  className="btn btn-sm btn-success"
                  onClick={addRow}
                  disabled={isSubmitting}
                >
                  <Plus size={16} className="me-1" />
                  {tr("payroll:add_row")}
                </button>
                <span className="ms-3 text-muted">
                  <small>
                    {rows.length} {" "}{tr("payroll:row_s")}{" "} {existingRowsCount} {" "}{tr("payroll:existing_2")}{" "}
                    {newValidRowsCount} {" "}{tr("payroll:new_valid_item_s")}
                  </small>
                </span>
              </div>

              {/* Sisi Kanan: Import/Export Buttons */}
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleDownloadTemplate}
                >
                  <i className="fas fa-download me-1"></i>{tr("shared:download_template")}
                </button>

                <label className="btn btn-sm btn-outline-primary mb-0 cursor-pointer">
                  <i className="fas fa-file-excel me-1"></i> {" "}{tr("payroll:import_excel")}
                  <input
                    type="file"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={handleImportExcel}
                  />
                </label>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "5%" }}>{tr("shared:status")}</th>
                    <th style={{ width: "18%" }}>{tr("payroll:email")}</th>
                    <th style={{ width: "14%" }}>{tr("payroll:item_name_2")}</th>
                    <th style={{ width: "14%" }}>{tr("payroll:value_formula")}</th>
                    <th style={{ width: "10%" }}>{tr("payroll:type")}</th>
                    <th style={{ width: "10%" }}>{tr("payroll:operator_2")}</th>
                    <th style={{ width: "10%" }}>{tr("payroll:is_taxable")}</th>
                    <th style={{ width: "14%" }}>{tr("payroll:group_name_2")}</th>
                    <th style={{ width: "5%" }}>{tr("payroll:action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor: row.isExisting
                          ? "#f8f9fa"
                          : "transparent",
                      }}
                    >
                      <td className="text-center align-middle">
                        {row.isExisting ? (
                          <span
                            className="badge bg-secondary"
                            style={{ fontSize: "10px" }}
                          >
                            {tr("payroll:existing")}
                          </span>
                        ) : (
                          <span
                            className="badge bg-success"
                            style={{ fontSize: "10px" }}
                          >
                            {tr("common:breadcrumb.new")}
                          </span>
                        )}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row.email}
                          onChange={(e) =>
                            updateRow(row.id, "email", e.target.value)
                          }
                          disabled={isSubmitting}
                        >
                          <option value="">{tr("payroll:select_email")}</option>
                          {payrollData.map((emp) => (
                            <option key={emp.email} value={emp.email}>
                              {emp.email} ({emp.name})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={tr("payroll:e_g_sales_incentive")}
                          value={row.itemName}
                          onChange={(e) =>
                            updateRow(row.id, "itemName", e.target.value)
                          }
                          disabled={isSubmitting}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={tr("payroll:e_g_500000")}
                          value={formatDisplayNumber(row.value)}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/\./g, ""); // Simpan tanpa titik di state
                            updateRow(row.id, "value", rawValue);
                          }}
                          disabled={isSubmitting}
                        />
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row.type}
                          onChange={(e) =>
                            updateRow(row.id, "type", e.target.value)
                          }
                          disabled={isSubmitting}
                        >
                          <option value="income">{tr("payroll:income")}</option>
                          <option value="deduction">{tr("payroll:deduction")}</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row.operator}
                          onChange={(e) =>
                            updateRow(row.id, "operator", e.target.value)
                          }
                          disabled={isSubmitting}
                        >
                          <option value="add">{tr("payroll:add_2")}</option>
                          <option value="subtract">{tr("payroll:subtract_2")}</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={row.isTaxable}
                          onChange={(e) =>
                            updateRow(row.id, "isTaxable", e.target.value)
                          }
                          disabled={isSubmitting}
                        >
                          <option value="false">{tr("payroll:false")}</option>
                          <option value="true">{tr("payroll:true")}</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={tr("payroll:e_g_additional_items")}
                          value={row.groupName}
                          onChange={(e) =>
                            updateRow(row.id, "groupName", e.target.value)
                          }
                          disabled={isSubmitting}
                        />
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteRow(row.id)}
                          disabled={isSubmitting}
                          title="Delete row"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="alert alert-warning mt-3">
              <small>
                <strong>{tr("payroll:formula_examples")}</strong>
                <br />{tr("payroll:fixed_value")}{" "} <code>500000</code>
                <br />{tr("payroll:percentage")}{" "} <code>attendance_pay * 10%</code>
                <br />{tr("payroll:complex")}{" "} <code>(employee_salary * 5%) + 100000</code>
              </small>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <i className="fas fa-times me-2"></i>
              {tr("common:actions.close")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || (!hasChanges && validRowsCount === 0)}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {tr("shared:processing")}
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2"></i>
                  {tr("payroll:apply_recalculate")}
                  {validRowsCount > 0
                    ? `${validRowsCount} item${validRowsCount !== 1 ? "s" : ""}`
                    : tr("payroll:remove_all_items")}
                  )
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
